import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useKlaviyoIntegrationUpdateMutation from "@babackapp/private-api-client/client/use-update-klaviyo-integration";
import useTestKlaviyoIntegration from "@babackapp/private-api-client/client/use-test-klaviyo-integration";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useTranslation from "@babackapp/translations/use-translation";
import { z } from "zod";
import IntegrationPreview from "./integration-preview";

const formSchema = z.object({
  activated: z.boolean(),
  token: z.string(),
});

const KlaviyoIntegration = () => {
  const t = useTranslation();

  const { data: shop } = useCurrentShopSuspense();
  const { activated, token } = shop.integrations.klaviyo;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { activated, token },
  });
  const { mutateAsync: updateKlaviyoIntegration } =
    useKlaviyoIntegrationUpdateMutation(shop);

  const { mutateAsync: testKlavioIntegration } = useTestKlaviyoIntegration(shop);

  const onSubmit = form.handleSubmit(async (data) => {
    return updateKlaviyoIntegration(data).then(() => form.reset(data));
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col justify-start gap-4">
        <FormField
          control={form.control}
          name="token"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.klaviyo.klaviyoToken")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={!form.formState.isDirty}>
          {t("misc.save")}
        </Button>

        <Button
          type="button"
          disabled={shop.integrations.klaviyo.token.length <= 0}
          onClick={() => testKlavioIntegration()}
          variant="outline"
        >
          {t("settings.integrations.thirdpart.klaviyo.initKlaviyo")}
        </Button>
      </form>
    </Form>
  );
};

export function KlaviyoIntegrationPreview() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateActivation } =
    useKlaviyoIntegrationUpdateMutation(shop);

  const onActivationUpdate = useCallback(
    (activated: boolean) => {
      if (!shop) return;
      updateActivation({ activated });
    },
    [shop, updateActivation],
  );

  if (!shop || !shop.plan.klaviyo) return null;

  return (
    <IntegrationPreview
      title="Klaviyo"
      bgColor="white"
      textColor="black"
      logo="/integrations/klaviyo.png"
      description={t("settings.integrations.thirdpart.klaviyo.description")}
      onActivationUpdate={onActivationUpdate}
      activated={shop.integrations.klaviyo.activated}
      seeMoreUrl="https://klaviyo.com/"
    >
      <KlaviyoIntegration />
    </IntegrationPreview>
  );
}
